import { createBrowserHistory, createHashHistory, History } from 'history';
import getAnalyticsHelper from './helpers/analyticsHelper';

export interface AppHistory extends History {
    canGoBackWithinApp: () => boolean;
}

export function isAppHistory(history: History): history is AppHistory {
    return 'canGoBackWithinApp' in history;
}

/**
 * Adds AppHistory functionality by tracking the current history depth.
 *
 * Note that this method doesn't work in environments that allow switching between history positions
 * e.g. by using history.go or similar.
 * This includes browsers, since they allow you to go back to any place in the history.
 */
export function depthTrackingAppHistoryEnhancer(history: History): AppHistory {
    let mobileAppHistoryDepth = 0;

    history.listen((location, action) => {
        switch (action) {
            case 'PUSH':
                mobileAppHistoryDepth++;
                break;
            case 'POP':
                mobileAppHistoryDepth--;
                break;
            case 'REPLACE':
            default:
                break;
        }
    });

    const canGoBackWithinApp = () => mobileAppHistoryDepth > 0;
    return Object.assign(history, {canGoBackWithinApp});
}

/**
 * Adds AppHistory functionality using a location state marker for the first visited page.
 *
 * Note that the provided history must support states i.e. you can't use hash history with this.
 */
export function firstPageMarkerAppHistoryEnhancer(history: History): AppHistory {
    const firstVisitedPageProperty = 'firstVisitedPage';

    const currentUrl = history.location.pathname + history.location.search + history.location.hash;
    const firstVisitedPageMarker = {[firstVisitedPageProperty]: true};
    history.replace(currentUrl, firstVisitedPageMarker);

    const canGoBackWithinApp = () =>
        !history.location.state || history.location.state[firstVisitedPageProperty] !== true;

    return Object.assign(history, {canGoBackWithinApp});
}

export function createAppHistory(
    baseHistory: History,
    appHistoryEnhancer: (history: History) => AppHistory
): AppHistory {

    const analyticsHelper = getAnalyticsHelper();
    baseHistory.listen(location => analyticsHelper.trackLocation(location));

    return appHistoryEnhancer(baseHistory);
}

/**
 * History object that will be used by the whole app.
 *
 * Note: React components should not import this directly.
 * Instead this dependency should be passed down from the root component as a prop using react-router helpers.
 * In certain cases this is hard and produces a lot of unnecessary code,
 * because the component is too deeply nested (e.g. GoBackLink, which can be places anywhere),
 * then use withRouter helper from react-router to inject the history.
 */
export default createAppHistory(
    // Older hash history is used for the mobile app,
    // because the current URL path should always resolve to the index.html file on mobile's filesystem.
    IS_MOBILE_APP ? createHashHistory() : createBrowserHistory(),
    IS_MOBILE_APP ? depthTrackingAppHistoryEnhancer : firstPageMarkerAppHistoryEnhancer
);

export function getRelativePathFromUrl(url: string): string {
    // Replaces everything up to the first slash which is not followed by another slash. e.g.
    // http://test.com/path => /path
    // //test.com/path => /path
    // /test/path => /test/path
    return url.replace(/^(?:\/\/|[^\/]+)*/, '');
}
